/** Codes de refus de licence renvoyés par l'API métier (session Cloud). */
export const CLOUD_LICENCE_REFUSAL_EVENT = 'nexxus:cloud-licence-refusal';

const MESSAGES: Record<string, string> = {
  license_revoked:
    'Cette licence a été révoquée. Vos données restent consultables et exportables, mais la saisie est bloquée. Contactez Nexxus.',
  license_suspended:
    'Cette licence est suspendue. Vos données restent consultables et exportables, mais la saisie est bloquée. Contactez Nexxus.',
  license_expired:
    'Cette licence a expiré. Vos données restent consultables et exportables, mais la saisie est bloquée. Contactez Nexxus pour la renouveler.',
  license_not_found: 'Clé de licence inconnue. Vérifiez la saisie.',
  server_unreachable: 'Serveur injoignable — vérifiez votre connexion Internet puis réessayez.',
  salary_limit_reached:
    'Le nombre maximal de salariés prévu par votre licence est atteint. Contactez Nexxus pour l’étendre.',
  user_limit_reached:
    'Le nombre maximal d’utilisateurs prévu par votre licence est atteint. Contactez Nexxus pour l’étendre.',
  payslip_locked:
    'Ce bulletin est validé ou payé : il est verrouillé et ne peut plus être modifié.',
  csrf_invalid: 'Votre session n’est plus à jour. Rechargez la page puis reconnectez-vous.',
  csrf_origin: 'Cette requête provient d’une origine non autorisée.',
  module_not_licensed:
    'Ce module n’est pas inclus dans votre licence. Contactez Nexxus pour l’ajouter.',
};

/** Message français simple pour un code de licence, ou undefined si le code n'est pas concerné. */
export function cloudLicenceMessage(code: string | undefined): string | undefined {
  return code ? MESSAGES[code] : undefined;
}

export type CloudLicenceRestrictionCode =
  'license_revoked' | 'license_suspended' | 'license_expired';

export function isCloudRestrictionCode(
  code: string | undefined,
): code is CloudLicenceRestrictionCode {
  return code === 'license_revoked' || code === 'license_suspended' || code === 'license_expired';
}

/** Libellé court d’un refus (activation web) : révoquée / suspendue / expirée. */
export function webLicenceStateLabel(code: string | undefined): string | null {
  if (code === 'license_revoked') return 'révoquée';
  if (code === 'license_suspended') return 'suspendue';
  if (code === 'license_expired') return 'expirée';
  return null;
}

/**
 * Message définitif pour l’écran d’activation Cloud (clé).
 * Couvre révoquée / suspendue / expirée / inconnue / serveur injoignable.
 */
export function webActivationErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code?: string }).code ?? '');
    // Activation : le libellé « activation impossible » prime sur le message de lecture seule
    // (il n’y a pas encore de données à consulter).
    const state = webLicenceStateLabel(code);
    if (state) {
      return `Cette licence est ${state} : l’activation d’un espace est impossible. Contactez Nexxus.`;
    }
    const known = cloudLicenceMessage(code);
    if (known) return known;
    if (code === 'local_mode') {
      return 'Cette licence n’est pas une licence Cloud : utilisez l’application installée sur votre poste.';
    }
  }
  if (err && typeof err === 'object' && 'statusCode' in err) {
    const status = Number((err as { statusCode?: number }).statusCode);
    if (status === 0 || status === 503 || status === 502 || status === 504) {
      return MESSAGES.server_unreachable ?? fallback;
    }
  }
  if (err instanceof TypeError || (err instanceof Error && /fetch|network|Failed to fetch/i.test(err.message))) {
    return MESSAGES.server_unreachable ?? fallback;
  }
  return fallback;
}

/** Refus 403 à la création du premier administrateur (clé non valide). */
export function webFirstAdminRefusalMessage(code: string | undefined): string {
  const state = webLicenceStateLabel(code);
  if (state) {
    return `Cette licence est ${state} : la création du premier administrateur exige une licence valide. Contactez Nexxus.`;
  }
  const known = cloudLicenceMessage(code);
  if (known) return known;
  return 'Clé de licence refusée : la création du premier administrateur exige une licence valide.';
}
